import React from 'react';
import API_BASE_URL from '../../config';
import { useAuth } from '../../context/AuthContext';
import ChatPanel from '../chat/ChatPanel';
import { CHIP_CSS } from '../chat/answerParts';
import { themeFromConfig } from '../../lib/theme';

/**
 * Widget & Persona tab (plan W7): per-site branding + behavior editing with a
 * LIVE preview — the real ChatPanel rendered with the draft config against the
 * partner's own corpus (asks work; telemetry suppressed), so what they see is
 * exactly what the client site gets. Saves via PUT /api/tenant/sites/<id>/config.
 */

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const input = {
  width: '100%', padding: '8px 10px', fontSize: '0.85rem', fontFamily: fontStack,
  borderRadius: '8px', border: '1px solid #e2e2e2', background: '#fafafa',
  color: '#1a1a1a', boxSizing: 'border-box',
};
const label = { fontSize: '0.78rem', color: '#555', display: 'block', margin: '10px 0 4px', fontWeight: 600 };

const WIDGET_FIELDS = {
  display_name: '', header_text: 'Ask a question', header_image_url: '',
  launcher_icon: 'chat', launcher_position: 'right',
  panel_width: 380, panel_height: 620, theme: 'light',
  accent_color: '#1e5eb8', font_family: 'system',
  starter_questions: [], input_placeholder: '', answer_disclaimer: '',
  terms_url: '', terms_label: 'Terms of use',
  sources_display: 'collapsed', audience_gate: false, audience_prompt: 'Which best describes you?',
};
const PERSONA_FIELDS = {
  style_preset: 'professional', refusal_message: '', refusal_message_es: '',
  followup_enabled: false, followup_text: 'Anything else I can help with?',
  contact_text: '', closing_text: '',
  answer_policy: 'standard', member_plans: [], member_statuses: [], reference_links: [],
};
const LINK_USES = {
  '': 'Any time it helps',
  plan: 'Answers that depend on the plan',
  portal: 'When the member must log in',
  forms: 'When a form is needed',
};
const MAX_REFERENCE_LINKS = 6;
// One label per line in the textarea; the server re-validates every label.
const toLines = (list) => (list || []).join('\n');
const fromLines = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 8);
const PRESET_HELP = {
  professional: 'The default voice: plain, factual, concise.',
  friendly: 'Adds a brief warm acknowledgment and encouraging phrasing.',
  concise: 'Cuts answers to 2-3 sentences wherever possible.',
  engaging: 'Mentions a clearly related published topic when it helps.',
};

function luminance(hex) {
  const h = (hex || '#000000').replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
const readableOn = (hex) => (luminance(hex) < 0.55 ? '#ffffff' : '#1a1a1a');

export default function WidgetPersonaTab() {
  const { channelUrl, customerKey } = useAuth();
  const [sites, setSites] = React.useState(null);
  const [siteId, setSiteId] = React.useState('');
  const [widget, setWidget] = React.useState({ ...WIDGET_FIELDS });
  const [persona, setPersona] = React.useState({ ...PERSONA_FIELDS });
  // Plan/status labels are edited as raw text (one per line) and become lists
  // on blur and on save: turning every keystroke into a trimmed list ate the
  // space in "Miscellaneous Safety" and every Enter.
  const [labelDrafts, setLabelDrafts] = React.useState({ member_plans: '', member_statuses: '' });
  const [errors, setErrors] = React.useState([]);
  const [status, setStatus] = React.useState('');
  const [audiences, setAudiences] = React.useState([]);
  const [audienceError, setAudienceError] = React.useState('');
  const [loadError, setLoadError] = React.useState('');

  const headers = React.useMemo(
    () => ({ 'Content-Type': 'application/json', 'X-Customer-Key': customerKey }),
    [customerKey]
  );

  const selectSite = React.useCallback((id, list) => {
    const site = (list || sites || []).find((s) => s.site_id === id);
    setSiteId(id);
    setWidget({ ...WIDGET_FIELDS, ...(site?.widget || {}) });
    setPersona({ ...PERSONA_FIELDS, ...(site?.persona || {}) });
    setLabelDrafts({
      member_plans: toLines(site?.persona?.member_plans),
      member_statuses: toLines(site?.persona?.member_statuses),
    });
    setErrors([]); setStatus('');
  }, [sites]);

  React.useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/api/tenant/sites`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body) => {
        if (!alive) return;
        setSites(body.sites);
        if (body.sites.length) selectSite(body.sites[0].site_id, body.sites);
      })
      .catch((e) => alive && setLoadError(`Could not load sites (${e.message}). Check your customer key.`));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const selectedCorpus = (sites || []).find((s) => s.site_id === siteId)?.channel_url || channelUrl;
  React.useEffect(() => {
    let alive = true;
    setAudiences([]); setAudienceError('');
    if (!selectedCorpus) return undefined;
    fetch(`${API_BASE_URL}/api/corpus/audiences?corpus_id=${encodeURIComponent(selectedCorpus)}`, { headers })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((body) => { if (alive) setAudiences(body.audiences); })
      .catch((e) => { if (alive) setAudienceError(`Could not load audiences: ${e.message}`); });
    return () => { alive = false; };
  }, [selectedCorpus, headers]);

  const save = async () => {
    setStatus('Saving…'); setErrors([]);
    const saved = {
      ...persona,
      member_plans: fromLines(labelDrafts.member_plans),
      member_statuses: fromLines(labelDrafts.member_statuses),
    };
    try {
      const r = await fetch(`${API_BASE_URL}/api/tenant/sites/${siteId}/config`, {
        method: 'PUT', headers, body: JSON.stringify({ widget, persona: saved }),
      });
      const body = await r.json();
      if (!r.ok) {
        setErrors(body.errors || [body.error || `HTTP ${r.status}`]);
        setStatus('');
        return;
      }
      setStatus(`Saved ${new Date(body.config_updated_at * 1000).toLocaleTimeString()} — live embeds update within a minute.`);
      setPersona(saved);
      setSites((s) => s.map((x) => (x.site_id === siteId ? { ...x, widget, persona: saved } : x)));
    } catch (e) {
      setErrors([`Save failed: ${e.message}`]); setStatus('');
    }
  };

  const w = (key) => ({
    value: widget[key] ?? '',
    onChange: (e) => setWidget({ ...widget, [key]: e.target.value }),
  });
  const setStarter = (i, value) => {
    const qs = [...(widget.starter_questions || [])];
    if (value === null) qs.splice(i, 1); else qs[i] = value;
    setWidget({ ...widget, starter_questions: qs });
  };
  const setLink = (i, patch) => {
    const links = [...(persona.reference_links || [])];
    if (patch === null) links.splice(i, 1); else links[i] = { label: '', url: '', use: '', ...links[i], ...patch };
    setPersona({ ...persona, reference_links: links });
  };

  // Preview config: fill the derived colors the server normally resolves.
  const preview = React.useMemo(() => {
    const accent = widget.accent_color || '#1e5eb8';
    return {
      ...widget, audiences,
      header_bg_color: widget.header_bg_color || accent,
      header_text_color: readableOn(widget.header_bg_color || accent),
      accent_text_color: readableOn(accent),
      bubble_user_bg: accent,
      bubble_user_text: readableOn(accent),
      bubble_bot_bg: widget.theme === 'dark' ? '#242424' : '#f3f4f6',
      bubble_bot_text: widget.theme === 'dark' ? '#e8e8e8' : '#1a1a1a',
    };
  }, [widget, audiences]);
  const c = themeFromConfig(preview, widget.theme);
  const contrastWarning = luminance(widget.accent_color || '#1e5eb8') > 0.62;

  if (loadError) return <p style={{ color: '#dc2626', fontSize: '0.9rem' }}>{loadError}</p>;
  if (!sites) return <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading sites…</p>;
  if (!sites.length) return <p style={{ color: '#888', fontSize: '0.9rem' }}>No embed sites registered for this key yet.</p>;

  const embedSnippet = [
    '<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>',
    '<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>',
    `<script src="${API_BASE_URL}/dist/clipt-widget.umd.js?v=${Date.now().toString(36)}"`,
    '  data-mode="chat"',
    `  data-site-id="${siteId}"`,
    `  data-backend-url="${API_BASE_URL}"></script>`,
  ].join('\n');

  return (
    <div style={{ fontFamily: fontStack }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="site-picker" style={{ ...label, margin: 0 }}>Site</label>
        <select id="site-picker" value={siteId} onChange={(e) => selectSite(e.target.value)}
                style={{ ...input, width: 'auto', minWidth: 220 }}>
          {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.site_id} — {s.channel_url}</option>)}
        </select>
        <button type="button" onClick={save}
                style={{ padding: '9px 22px', fontWeight: 600, borderRadius: 8, border: 'none',
                         background: '#111', color: '#fff', cursor: 'pointer', fontFamily: fontStack }}>
          Save
        </button>
        {status && <span role="status" style={{ fontSize: '0.8rem', color: '#15803d' }}>{status}</span>}
      </div>
      {errors.length > 0 && (
        <div role="alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                                   padding: '10px 14px', marginBottom: 14, fontSize: '0.82rem', color: '#b91c1c' }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 28, alignItems: 'start' }}>
        {/* -------- form column -------- */}
        <div>
          <h3 style={{ fontSize: '0.95rem', margin: '0 0 4px' }}>Branding</h3>
          <label style={label}>Bot name (header)
            <input {...w('display_name')} maxLength={60} style={input} placeholder="e.g. SDCERA Member Assistant" /></label>
          <label style={label}>Header subtitle
            <input {...w('header_text')} maxLength={80} style={input} /></label>
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={label}>Accent color
              <input type="color" value={widget.accent_color || '#1e5eb8'}
                     onChange={(e) => setWidget({ ...widget, accent_color: e.target.value })}
                     style={{ display: 'block', width: 56, height: 32, padding: 2, border: '1px solid #e2e2e2', borderRadius: 6 }} /></label>
            <label style={label}>Launcher icon
              <select value={widget.launcher_icon || 'chat'}
                      onChange={(e) => setWidget({ ...widget, launcher_icon: e.target.value })} style={input}>
                <option value="chat">Chat bubble</option>
                <option value="question">Question mark</option>
                <option value="sparkle">Sparkle</option>
              </select></label>
            <label style={label}>Position
              <select value={widget.launcher_position || 'right'}
                      onChange={(e) => setWidget({ ...widget, launcher_position: e.target.value })} style={input}>
                <option value="right">Bottom right</option>
                <option value="left">Bottom left</option>
              </select></label>
            <label style={label}>Sources under each answer
              <select value={widget.sources_display || 'collapsed'}
                      onChange={(e) => setWidget({ ...widget, sources_display: e.target.value })} style={input}>
                <option value="collapsed">Collapsed until tapped</option>
                <option value="expanded">Expanded</option>
                <option value="cited">Cited sources only</option>
              </select></label>
          </div>
          {contrastWarning && (
            <p style={{ fontSize: '0.75rem', color: '#b45309', margin: '6px 0 0' }}>
              Light accent: white text on this color may fail contrast — consider a darker shade.
            </p>
          )}
          <label style={label}>Header background image URL (optional)
            <input {...w('header_image_url')} style={input} placeholder="https://…" /></label>
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={label}>Panel width ({widget.panel_width || 380}px)
              <input type="range" min={320} max={480} value={widget.panel_width || 380}
                     onChange={(e) => setWidget({ ...widget, panel_width: parseInt(e.target.value, 10) })}
                     style={{ width: '100%' }} /></label>
            <label style={label}>Panel height ({widget.panel_height || 620}px)
              <input type="range" min={480} max={760} value={widget.panel_height || 620}
                     onChange={(e) => setWidget({ ...widget, panel_height: parseInt(e.target.value, 10) })}
                     style={{ width: '100%' }} /></label>
            <label style={label}>Font
              <select value={widget.font_family || 'system'}
                      onChange={(e) => setWidget({ ...widget, font_family: e.target.value })} style={input}>
                <option value="system">System</option>
                <option value="inter">Inter</option>
                <option value="georgia">Georgia</option>
                <option value="arial">Arial</option>
              </select></label>
          </div>

          <h3 style={{ fontSize: '0.95rem', margin: '20px 0 4px' }}>Content</h3>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '12px 0' }}>
            <input type="checkbox" checked={!!widget.audience_gate} disabled={!audiences.length}
              onChange={(e) => setWidget({ ...widget, audience_gate: e.target.checked })} />
            Ask visitors who they are before the first question
          </label>
          <p style={{ fontSize: 12, color: '#777' }}>Audiences and per-audience starter questions are managed in Sources.</p>
          {!audiences.length && <p style={{ fontSize: 12, color: '#777' }}>{audienceError || 'Define audiences in Sources before enabling the gate.'}</p>}
          <label style={label}>Audience prompt
            <input {...w('audience_prompt')} required maxLength={120} style={input} /></label>
          <span style={label}>Starter questions (up to 4)</span>
          {(widget.starter_questions || []).map((q, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input value={q} maxLength={120} onChange={(e) => setStarter(i, e.target.value)} style={input}
                     aria-label={`Starter question ${i + 1}`} />
              <button type="button" onClick={() => setStarter(i, null)} aria-label={`Remove starter question ${i + 1}`}
                      style={{ border: '1px solid #e2e2e2', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          {(widget.starter_questions || []).length < 4 && (
            <button type="button" onClick={() => setStarter((widget.starter_questions || []).length, '')}
                    style={{ fontSize: '0.78rem', border: '1px dashed #ccc', background: '#fff', borderRadius: 6,
                             padding: '5px 10px', cursor: 'pointer', fontFamily: fontStack }}>
              + Add question
            </button>
          )}
          <label style={label}>Input placeholder
            <input {...w('input_placeholder')} maxLength={80} style={input} placeholder="Ask about your benefits…" /></label>
          <label style={label}>Per-answer disclaimer ({(widget.answer_disclaimer || '').length}/300)
            <textarea {...w('answer_disclaimer')} maxLength={300} rows={2} style={{ ...input, resize: 'vertical' }} /></label>
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={{ ...label, flex: 2 }}>Terms link URL
              <input {...w('terms_url')} style={input} placeholder="https://…" /></label>
            <label style={{ ...label, flex: 1 }}>Terms label
              <input {...w('terms_label')} maxLength={40} style={input} /></label>
          </div>

          <h3 style={{ fontSize: '0.95rem', margin: '20px 0 4px' }}>Persona</h3>
          <label style={label}>Communication style
            <select value={persona.style_preset || 'professional'}
                    onChange={(e) => setPersona({ ...persona, style_preset: e.target.value })} style={input}>
              {Object.keys(PRESET_HELP).map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select></label>
          <p style={{ fontSize: '0.75rem', color: '#777', margin: '4px 0 0' }}>{PRESET_HELP[persona.style_preset || 'professional']}</p>
          <label style={label}>Custom "I don't know" message ({(persona.refusal_message || '').length}/400)
            <textarea value={persona.refusal_message || ''} maxLength={400} rows={3}
                      onChange={(e) => setPersona({ ...persona, refusal_message: e.target.value })}
                      placeholder={'Leave empty for the default. {agency} and {contact} are replaced automatically.'}
                      style={{ ...input, resize: 'vertical' }} /></label>
          <label style={label}>Spanish variant (optional)
            <textarea value={persona.refusal_message_es || ''} maxLength={400} rows={2}
                      onChange={(e) => setPersona({ ...persona, refusal_message_es: e.target.value })}
                      style={{ ...input, resize: 'vertical' }} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!persona.followup_enabled}
                   onChange={(e) => setPersona({ ...persona, followup_enabled: e.target.checked })}
                   style={{ width: 15, height: 15, accentColor: '#111' }} />
            <span style={{ fontSize: '0.82rem', color: '#333' }}>End answered questions with a follow-up line</span>
          </label>
          {persona.followup_enabled && (
            <input value={persona.followup_text || ''} maxLength={120}
                   onChange={(e) => setPersona({ ...persona, followup_text: e.target.value })}
                   aria-label="Follow-up text" style={input} />
          )}
          <label style={label}>How to reach you ({(persona.contact_text || '').length}/160)
            <input value={persona.contact_text || ''} maxLength={160}
                   onChange={(e) => setPersona({ ...persona, contact_text: e.target.value })}
                   placeholder="e.g. 415-487-7000 or sfersconnect@sfgov.org" style={input} /></label>
          <p style={{ fontSize: '0.75rem', color: '#777', margin: '4px 0 0' }}>
            Fills {'{contact}'} in every message the assistant sends when it can't answer.
          </p>
          <label style={label}>Closing line on every reply ({(persona.closing_text || '').length}/300)
            <textarea value={persona.closing_text || ''} maxLength={300} rows={2}
                      onChange={(e) => setPersona({ ...persona, closing_text: e.target.value })}
                      placeholder="e.g. If you need further assistance, please contact us directly at …"
                      style={{ ...input, resize: 'vertical' }} /></label>

          <h3 style={{ fontSize: '0.95rem', margin: '20px 0 4px' }}>Answer rules</h3>
          <label style={label}>Rules
            <select value={persona.answer_policy || 'standard'}
                    onChange={(e) => setPersona({ ...persona, answer_policy: e.target.value })} style={input}>
              <option value="standard">Standard</option>
              <option value="member_services">Member services</option>
            </select></label>
          <p style={{ fontSize: '0.75rem', color: '#777', margin: '4px 0 0' }}>
            Member services: asks for the member's plan or status when the answer depends on it, links pages
            and forms by name, answers from web pages and FAQs first, and gives form submission steps.
          </p>
          {persona.answer_policy === 'member_services' && (
            <>
              <label style={label}>Plans the assistant can ask about (one per line)
                <textarea value={labelDrafts.member_plans} rows={3}
                          onChange={(e) => setLabelDrafts({ ...labelDrafts, member_plans: e.target.value })}
                          onBlur={() => setPersona({ ...persona, member_plans: fromLines(labelDrafts.member_plans) })}
                          placeholder={'Miscellaneous\nSafety'} style={{ ...input, resize: 'vertical' }} /></label>
              <label style={label}>Member statuses (one per line)
                <textarea value={labelDrafts.member_statuses} rows={3}
                          onChange={(e) => setLabelDrafts({ ...labelDrafts, member_statuses: e.target.value })}
                          onBlur={() => setPersona({ ...persona, member_statuses: fromLines(labelDrafts.member_statuses) })}
                          placeholder={'Active\nRetired'} style={{ ...input, resize: 'vertical' }} /></label>
              <span style={label}>Reference links (up to {MAX_REFERENCE_LINKS})</span>
              {(persona.reference_links || []).map((link, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, marginBottom: 6 }}>
                  <input value={link.label || ''} maxLength={60} placeholder="Name, e.g. Member portal login"
                         onChange={(e) => setLink(i, { label: e.target.value })} style={input}
                         aria-label={`Reference link ${i + 1} name`} />
                  <input value={link.url || ''} placeholder="https://…"
                         onChange={(e) => setLink(i, { url: e.target.value })} style={input}
                         aria-label={`Reference link ${i + 1} URL`} />
                  <button type="button" onClick={() => setLink(i, null)} aria-label={`Remove reference link ${i + 1}`}
                          style={{ border: '1px solid #e2e2e2', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                  <select value={link.use || ''} onChange={(e) => setLink(i, { use: e.target.value })}
                          aria-label={`Reference link ${i + 1} use`} style={{ ...input, gridColumn: '1 / 3' }}>
                    {Object.entries(LINK_USES).map(([v, t]) => <option key={v} value={v}>Link it: {t}</option>)}
                  </select>
                </div>
              ))}
              {(persona.reference_links || []).length < MAX_REFERENCE_LINKS && (
                <button type="button" onClick={() => setLink((persona.reference_links || []).length, {})}
                        style={{ fontSize: '0.78rem', border: '1px dashed #ccc', background: '#fff', borderRadius: 6,
                                 padding: '5px 10px', cursor: 'pointer', fontFamily: fontStack }}>
                  + Add link
                </button>
              )}
            </>
          )}

          <h3 style={{ fontSize: '0.95rem', margin: '20px 0 4px' }}>Embed snippet</h3>
          <pre style={{ background: '#f6f7f7', border: '1px solid #e2e2e2', borderRadius: 8, padding: 10,
                        fontSize: '0.7rem', overflowX: 'auto' }}>{embedSnippet}</pre>
          <button type="button"
                  onClick={() => navigator.clipboard && navigator.clipboard.writeText(embedSnippet)}
                  style={{ fontSize: '0.78rem', border: '1px solid #e2e2e2', background: '#fff', borderRadius: 6,
                           padding: '5px 10px', cursor: 'pointer', fontFamily: fontStack }}>
            Copy snippet
          </button>
        </div>

        {/* -------- live preview column -------- */}
        <div>
          <h3 style={{ fontSize: '0.95rem', margin: '0 0 8px' }}>
            Live preview <span style={{ fontWeight: 400, color: '#888', fontSize: '0.78rem' }}>(answers use your real corpus)</span>
          </h3>
          <div className="inquiros-chat" style={{ position: 'sticky', top: 16 }}>
            <style>{CHIP_CSS(c)}</style>
            <div style={{ width: Math.min(preview.panel_width || 380, 460), height: Math.min(preview.panel_height || 620, 640),
                          boxShadow: '0 12px 40px rgba(0,0,0,.18)', borderRadius: 12 }}>
              <ChatPanel
                key={siteId} backendUrl={API_BASE_URL} channelUrl={selectedCorpus} customerKey={customerKey}
                agencyName={channelUrl.replace(/^https?:\/\/(www\.)?/, '').split('.')[0].toUpperCase()}
                tracker={null} c={c} config={preview}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
